/**
 * Root. AuthProvider boots the session; unauthenticated → LoginPage,
 * authenticated → the routed app shell with the real screens.
 *
 * One login for everyone — no role choice on the front door. After sign-in the
 * user's role (resolved server-side) decides which route tree + nav they get.
 * A client can't reach /coach/* and a coach can't reach the client tabs; both
 * fall through to their own home.
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider, useAuth } from '@/lib/auth';
import AppShell from '@/components/AppShell';
import LoginPage from '@/pages/LoginPage';

// Client screens
import HomeDashboard from '@/pages/HomeDashboard';
import AppointmentsPage from '@/pages/AppointmentsPage';
import CalendarPage from '@/pages/CalendarPage';
import ProgressDetail from '@/pages/ProgressDetail';
import WorkoutsList from '@/pages/WorkoutsList';
import WorkoutLog from '@/pages/WorkoutLog';
import WorkoutHistory from '@/pages/WorkoutHistory';
import CardioLog from '@/pages/CardioLog';
import CustomWorkout from '@/pages/CustomWorkout';
import ProgramDetail from '@/pages/ProgramDetail';
import NutritionDashboard from '@/pages/NutritionDashboard';
import NutritionManual from '@/pages/NutritionManual';
import NutritionSearch from '@/pages/NutritionSearch';
import BarcodeScan from '@/pages/BarcodeScan';
import ProfileMenu from '@/pages/ProfileMenu';
import Testimonial from '@/pages/Testimonial';
import Settings from '@/pages/Settings';
import AiCoach from '@/pages/AiCoach';
import Credits from '@/pages/Credits';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import Measurements from '@/pages/Measurements';
import MeasurementAdd from '@/pages/MeasurementAdd';
import HealthData from '@/pages/HealthData';
import MessagesPage from '@/pages/MessagesPage';

/**
 * Cross-app handoff. The admin app (admin.regisathletics.com) sends users here
 * to sign in with `?next=<full admin URL>`. Once authenticated, bounce the
 * browser to that URL. Only same-registrable-domain hosts are allowed, so this
 * can't be used as an open redirect.
 */
function consumeNextRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next) return false;
  try {
    const url = new URL(next, window.location.origin);
    const sameSite =
      url.hostname === window.location.hostname ||
      url.hostname.endsWith('.regisathletics.com') ||
      url.hostname === 'regisathletics.com';
    if (!sameSite) return false;
    // Don't bounce to ourselves (would loop).
    if (url.origin === window.location.origin) return false;
    window.location.replace(url.toString());
    return true;
  } catch {
    return false;
  }
}

function Gate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-bg" style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', color: 'var(--gray-400)' }}>
        Loading…
      </div>
    );
  }

  if (!user) return <LoginPage />;

  // Logged in. If we were sent here to hand off to another regisathletics.com
  // app (e.g. admin), go there instead of rendering the in-app routes.
  if (consumeNextRedirect()) {
    return (
      <div className="app-bg" style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', color: 'var(--gray-400)' }}>
        Redirecting…
      </div>
    );
  }

  // The SPA is the end-user (client) experience for EVERYONE. Trainer/admin
  // management lives in the admin app, not here. A trainer who signs into the
  // SPA just sees the client app.
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/home" element={<HomeDashboard />} />
        <Route path="/home/appointments" element={<AppointmentsPage />} />
        <Route path="/home/progress" element={<ProgressDetail />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/workouts" element={<WorkoutsList />} />
        <Route path="/workouts/log" element={<WorkoutLog />} />
        <Route path="/workouts/history" element={<WorkoutHistory />} />
        <Route path="/workouts/cardio" element={<CardioLog />} />
        <Route path="/workouts/custom" element={<CustomWorkout />} />
        <Route path="/workouts/program/:id" element={<ProgramDetail />} />
        <Route path="/nutrition" element={<NutritionDashboard />} />
        <Route path="/nutrition/manual" element={<NutritionManual />} />
        <Route path="/nutrition/search" element={<NutritionSearch />} />
        <Route path="/nutrition/scan" element={<BarcodeScan />} />
        <Route path="/community" element={<Blog />} />
        <Route path="/community/:slug" element={<BlogPost />} />
        <Route path="/profile" element={<ProfileMenu />} />
        <Route path="/profile/measurements" element={<Measurements />} />
        <Route path="/profile/measurements/add" element={<MeasurementAdd />} />
        <Route path="/profile/health" element={<HealthData />} />
        <Route path="/profile/messages" element={<MessagesPage />} />
        <Route path="/profile/ai-coach" element={<AiCoach />} />
        <Route path="/profile/credits" element={<Credits />} />
        <Route path="/profile/testimonial" element={<Testimonial />} />
        <Route path="/profile/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Gate />
      </BrowserRouter>
    </AuthProvider>
  );
}
